import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, GripVertical, Upload, LogOut } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "nv_admin_token";

const resolveImageUrl = (url) =>
  url.startsWith("/uploads") ? `${BACKEND_URL}${url}` : url;

export default function AdminGallery() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragIndex = useRef(null);
  const fileInputRef = useRef(null);

  const verify = async (candidateToken) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": candidateToken } });
      return true;
    } catch {
      return false;
    }
  };

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setImages((res.data || []).sort((a, b) => a.order - b.order));
    } catch (e) {
      console.error("Failed to load gallery", e);
      toast.error("Couldn't load gallery images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (token) {
        const ok = await verify(token);
        if (ok) {
          setAuthed(true);
          fetchImages();
        } else {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
        }
      }
      setCheckingAuth(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    const ok = await verify(password);
    setLoggingIn(false);
    if (ok) {
      sessionStorage.setItem(TOKEN_KEY, password);
      setToken(password);
      setPassword("");
      setAuthed(true);
      fetchImages();
    } else {
      toast.error("Incorrect password.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/admin/gallery/upload`, formData, {
          headers: { "X-Admin-Token": token },
        });
        uploaded.push(res.data);
      }
      setImages((prev) => [...prev, ...uploaded]);
      toast.success(
        uploaded.length === 1 ? "Image added." : `${uploaded.length} images added.`
      );
    } catch (err) {
      console.error(err);
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setImages((prev) => prev.filter((img) => img.id !== id));
      toast.success("Image removed.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't remove image.");
    }
  };

  const saveOrder = async (ordered) => {
    setSavingOrder(true);
    try {
      await axios.put(
        `${API}/admin/gallery/reorder`,
        { order: ordered.map((img) => img.id) },
        { headers: { "X-Admin-Token": token } }
      );
    } catch (err) {
      console.error(err);
      toast.error("Couldn't save new order.");
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDrop = (dropIndex) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === dropIndex) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(dropIndex, 0, moved);
      saveOrder(next);
      return next;
    });
  };

  if (checkingAuth) {
    return (
      <div className="nv-section pt-32 flex justify-center">
        <Loader2 className="animate-spin text-brand-inkMuted" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div data-testid="page-admin-gallery-login" className="nv-section pt-32">
        <div className="nv-container max-w-sm mx-auto">
          <p className="nv-overline mb-4">Admin</p>
          <h1 className="font-heading text-3xl mb-8">Gallery access</h1>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className="rounded-none border-brand-line focus-visible:ring-brand-navy focus-visible:border-brand-navy"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loggingIn}
              className="nv-btn-primary w-full disabled:opacity-60"
            >
              {loggingIn ? <Loader2 size={16} className="animate-spin" /> : "Enter"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-admin-gallery">
      <section className="nv-section pt-32 pb-10">
        <div className="nv-container flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="nv-overline mb-4">Admin</p>
            <h1 className="font-heading text-4xl sm:text-5xl leading-tight">
              Manage gallery
            </h1>
            <p className="mt-4 text-brand-inkMuted text-sm max-w-lg">
              Drag a photo to reorder it. Changes save automatically.
              {savingOrder && " Saving…"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              className="hidden"
              data-testid="admin-upload-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="admin-upload-trigger"
              className="nv-btn-primary disabled:opacity-60"
            >
              {uploading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Upload size={16} />
              )}
              Add image
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="nv-btn-secondary"
            >
              <LogOut size={16} />
              Log out
            </button>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="nv-container">
          {loading ? (
            <p className="text-brand-inkMuted">Loading…</p>
          ) : images.length === 0 ? (
            <p className="text-brand-inkMuted">No images yet — add one above.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {images.map((img, idx) => (
                <figure
                  key={img.id}
                  draggable
                  onDragStart={() => (dragIndex.current = idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(idx)}
                  data-testid={`admin-gallery-item-${idx}`}
                  className="relative group bg-brand-surfaceAlt border border-brand-line cursor-grab active:cursor-grabbing"
                >
                  <img
                    src={resolveImageUrl(img.url)}
                    alt=""
                    className="w-full h-40 object-cover pointer-events-none"
                  />
                  <div className="absolute top-2 left-2 bg-white/90 text-brand-ink text-xs px-2 py-1 flex items-center gap-1">
                    <GripVertical size={12} />
                    {idx + 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(img.id)}
                    aria-label="Remove image"
                    data-testid={`admin-gallery-delete-${idx}`}
                    className="absolute top-2 right-2 bg-white/90 hover:bg-red-600 hover:text-white text-brand-ink p-1.5 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
